"use client";

import {
  Field,
  NextImage,
  withDatasourceCheck,
  ImageField,
  Text,
  LinkField,
} from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import React, { useEffect, useRef, MouseEvent } from "react";
import * as Flex from "@repo/ui/flex";
import {
  TYPOGRAPHY_DISPLAY1,
  TYPOGRAPHY_HEADING2,
  TYPOGRAPHY_LINK1_CAPS,
} from "@/theme/topshop-2/typography/typography-constants";
import { ComponentProps } from "../../types/component-props";
import { appendRef, isMobile } from "../utils";
import { VideoControls } from "../video-controls";
import {
  TextAlignmentType,
  GradientType,
  JustifyType,
  AlignType,
} from "./utils";
import {
  getVerticalAlignment,
  getHeadingAlignment,
  getHorizontalAlignment,
} from "./utils";
import classes from "./media-cta.module.css";

export type MediaCtaFieldsProps = {
  id?: string;
  enableSound: Field<boolean> | { jsonValue: Field<boolean> };
  gradient: Field<string>;
  textAlignment?: Field<string>;
  headline: Field<string>;
  subheading: Field<string>;
  textColour: Field<string>;
  mobileImage: ImageField;
  desktopImage: ImageField;
  mobileVideo: Field<string>;
  desktopVideo: Field<string>;
  ctaLink: LinkField | { jsonValue: LinkField };
  ctaText: Field<string>;
  ctaRef: Field<string>;
  mobileTextAlignment?: Field<string>;
  desktopTextAlignment?: Field<string>;
  displayGradientOnlyOnHover: Field<boolean> | { jsonValue: Field<boolean> };
};

export type MediaCtaProps = ComponentProps & {
  isActive?: boolean;
  id?: string;
  fields: MediaCtaFieldsProps;
};

const MediaCta = (props: MediaCtaProps) => {
  const {
    id,
    isActive,
    fields: {
      id: ctaId,
      enableSound,
      desktopImage,
      mobileImage,
      desktopVideo,
      mobileVideo,
      headline,
      ctaText,
      ctaLink,
      ctaRef,
      subheading,
      textAlignment,
      mobileTextAlignment,
      desktopTextAlignment,
      gradient,
      textColour,
      displayGradientOnlyOnHover,
    },
    rendering: { componentName },
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const ctaLinkRef = useRef<HTMLAnchorElement>(null);
  const textContainer = useRef<HTMLDivElement>(null);

  const mobileView = isMobile();

  const isHero = !componentName.match(/cta/i);
  const isVideo = desktopVideo.value;
  const mediaImage = mobileView ? mobileImage : desktopImage;
  const mediaVideo = mobileView ? mobileVideo : desktopVideo;

  const textAlignmentValue = isHero
    ? mobileView
      ? (mobileTextAlignment?.value.toLocaleLowerCase() as TextAlignmentType)
      : (desktopTextAlignment?.value.toLocaleLowerCase() as TextAlignmentType)
    : (textAlignment?.value.toLocaleLowerCase() as TextAlignmentType);

  const verticalAlignment = getVerticalAlignment(textAlignmentValue);
  const horizontalAlignment = getHorizontalAlignment(textAlignmentValue);
  const headingTextAlignment = getHeadingAlignment(textAlignmentValue);

  const gradientValue = gradient?.value.toLocaleLowerCase() as GradientType;

  const showGradientOnHover =
    (displayGradientOnlyOnHover && "jsonValue" in displayGradientOnlyOnHover
      ? displayGradientOnlyOnHover?.jsonValue?.value
      : displayGradientOnlyOnHover?.value) || false;

  const gradientOptions = {
    "all overlays": showGradientOnHover ? "overlayWithHover" : "overlay",
    "layer overlay": "layerOverlay",
    "no overlays": "",
  };

  const ctaClickHandler = (
    e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLDivElement>,
  ) => {
    e.stopPropagation();
    const link =
      ctaLink && "jsonValue" in ctaLink ? ctaLink.jsonValue : ctaLink;

    const href = link.value.href;

    if (href) {
      const url = appendRef(href, ctaRef.value);

      if (
        link.value.linktype === "external" &&
        typeof link.value.target === "string" &&
        link.value.target !== ""
      ) {
        window.open(url, link.value.target);
      } else {
        window.location.href = url;
      }
    }
  };

  const textContent = (
    <>
      <Text
        data-testid="headline"
        field={headline}
        tag="h2"
        className={clsx(
          TYPOGRAPHY_DISPLAY1,
          classes[!isHero ? "ctaTitle" : "heroCtaTitle"],
        )}
        style={{
          color: textColour.value || undefined,
        }}
      />
      {isHero && subheading?.value && (
        <Text
          data-testid="sub-headline"
          field={subheading}
          tag="p"
          className={clsx(
            TYPOGRAPHY_HEADING2,
            classes[mobileView ? "ctaSubtitle" : "heroCtaSubtitle"],
          )}
          style={{
            color: textColour.value || undefined,
          }}
        />
      )}

      {((mobileView && isHero) || !isHero) && (
        <a
          onClick={ctaClickHandler}
          data-testid="cta-link"
          style={{ color: textColour.value || undefined }}
          className={clsx(
            TYPOGRAPHY_LINK1_CAPS,
            classes[!mobileView && isHero ? "desktopHeroCtaLink" : "ctaLink"],
          )}
          ref={ctaLinkRef}
        >
          <Text field={ctaText} tag="span" />
        </a>
      )}
    </>
  );

  const wrappedTextContent = (
    <div
      className={clsx(
        classes.textContentWrapper,
        classes[
          headingTextAlignment === "right"
            ? "textAlignRight"
            : "textAlignCenter"
        ],
      )}
    >
      {textContent}
    </div>
  );

  const mediaContainerClass = [
    classes.mediaContainer,
    mobileView && classes.visible,
    gradientValue && classes[gradientOptions[gradientValue]!],
    !mobileView && isHero && classes.heroMediaContainer,
  ];

  useEffect(() => {
    const underline = document.createElement("span");

    underline.style.position = "absolute";
    underline.style.bottom = "0";
    underline.style.height = "1px";
    underline.style.backgroundColor =
      textColour.value || "var(--color-stroke-secondary)";
    underline.style.left = "0";
    underline.style.width = "100%";

    if (ctaLinkRef.current && ctaLinkRef.current.childNodes.length < 2) {
      ctaLinkRef.current.appendChild(underline);
    }
  }, [mobileView]);

  const carouselAccessibilityProps = {
    "aria-hidden": !isActive,
    tabIndex: isActive ? 0 : -1,
    "data-label": "carousel-slide",
  };

  const mediaCtaAccessibilityProps = {
    role: "link",
    tabIndex: 0,
    "aria-label": ctaText.value || "Learn more",
  };

  const accessibility =
    componentName === "Hero Carousel"
      ? carouselAccessibilityProps
      : mediaCtaAccessibilityProps;

  const link = ctaLink && "jsonValue" in ctaLink ? ctaLink.jsonValue : ctaLink;

  return (
    <div
      {...accessibility}
      id={id || ctaId || ""}
      style={{ cursor: link.value.href ? "pointer" : "default" }}
      data-testid="media-container"
      className={clsx(mediaContainerClass)}
      onClick={ctaClickHandler}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();

          const href = link.value.href;

          if (href) {
            const url = appendRef(href, ctaRef.value);

            if (
              link.value.linktype === "external" &&
              typeof link.value.target === "string" &&
              link.value.target !== ""
            ) {
              window.open(url, link.value.target);
            } else {
              window.location.href = url;
            }
          }
        }
      }}
      onFocus={() => {
        textContainer.current?.classList.add(classes.visible!);
      }}
      onBlur={() => {
        textContainer.current?.classList.remove(classes.visible!);
      }}
    >
      {isVideo ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          ref={videoRef}
          className={classes.media}
          src={mediaVideo.value}
          data-active={isActive}
          data-id={id || ctaId || ""}
        ></video>
      ) : (
        <NextImage
          field={mediaImage}
          className={classes.media}
          alt={mediaImage?.value?.alt as string}
          unoptimized={true}
        />
      )}
      <Flex.Container
        column
        className={
          classes[
            mobileView || !isHero
              ? "ctaTextContainerWrapper"
              : "heroCtaTextContainerWrapper"
          ]
        }
      >
        <Flex.Container
          column
          className={
            classes[
              mobileView || !isHero
                ? "ctaTextContainerWrapper"
                : "heroCtaTextContainerWrapper"
            ]
          }
        >
          <Flex.Container
            column
            ref={textContainer}
            data-testid="text-container"
            className={
              classes[
                mobileView || isHero
                  ? "heroCtaTextContainer"
                  : "ctaTextContainer"
              ]
            }
            justify={verticalAlignment as JustifyType}
            align={horizontalAlignment as AlignType}
          >
            {isHero && !mobileView ? wrappedTextContent : textContent}
          </Flex.Container>
          {!mobileView && isHero && (
            <Flex.Container justify="center">
              <a
                onClick={ctaClickHandler}
                data-testid="cta-link"
                style={
                  textColour.value ? { color: textColour.value } : undefined
                }
                className={clsx(
                  TYPOGRAPHY_LINK1_CAPS,
                  classes.desktopHeroCtaLink,
                )}
                ref={ctaLinkRef}
              >
                <Text field={ctaText} tag="span" />
              </a>
            </Flex.Container>
          )}
        </Flex.Container>
        {isVideo && (
          <VideoControls
            hasSound={
              !!(
                enableSound && "jsonValue" in enableSound
                  ? enableSound.jsonValue
                  : enableSound
              )?.value
            }
            size="lg"
            videoRef={videoRef}
            isActive={isActive as boolean}
            componentName={componentName}
          />
        )}
      </Flex.Container>
    </div>
  );
};

export default withDatasourceCheck()<MediaCtaProps>(MediaCta);
